import React, { type ReactNode, useEffect, useRef } from 'react'
import clsx from 'clsx'
import type { Transition, Variants } from 'motion/react'
import { motion } from 'motion/react'
import { CheckIcon } from 'ui/src/icons/CheckIcon'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { framerTransitions } from 'ui/src/theme/transitions'
import type { FontSizeProps, FontSizes } from 'ui/src/theme/typography'
import { list, listItem, tickerItemWrapper } from './Ticker.css'

export type TickerHeight = {
  tickerHeight: FontSizes
  tickerHeightDesktop: FontSizes
}

type Props = {
  children: ReactNode
  showCheckIcon?: boolean
  size: FontSizeProps
}

export function Ticker(props: Props) {
  // Font size is used as height for the ticker to make animation of items responsive
  const classNames = clsx(list, sprinkles({ fontSize: props.size }))

  return <ul className={classNames}>{props.children}</ul>
}

const DURATION = 0.5
const DRIFT_HEIGHT = '0.8em'
const ANIMATION: Variants = {
  pushDown: { y: DRIFT_HEIGHT, opacity: 0 },
  original: { y: 0, opacity: 1 },
  pushUp: { y: `-${DRIFT_HEIGHT}`, opacity: 0 },
}
const TRANSITION: Transition = { duration: DURATION, ...framerTransitions.easeInOutQuint }

type TickerItemProps = {
  children: React.ReactNode
  showCheckIcon?: boolean
}

export function TickerItem(props: TickerItemProps) {
  return (
    <motion.li
      className={listItem}
      transition={TRANSITION}
      variants={ANIMATION}
      initial="pushDown"
      animate="original"
      exit="pushUp"
    >
      <div className={tickerItemWrapper}>
        {/* Optical alignment for the check icon */}
        {props.showCheckIcon && <CheckIcon size="1rem" style={{ marginTop: -1 }} />}
        {props.children}
      </div>
    </motion.li>
  )
}

export const useTickerAnimation = (callback: () => void, isInView: boolean, delay = 2000) => {
  // Store timeout ref to clear it on unmount or when the element is not in view
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const animate = () => {
      timeoutRef.current = setTimeout(() => {
        callback()
        animate()
      }, delay)
    }

    if (isInView) {
      animate()
    } else {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current)
      }
    }

    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [delay, isInView, callback])
}
